import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../lib/api";
import EconWidget from "./EconWidget";
import WidgetGallery from "./WidgetGallery";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, GripVertical, X, ChevronUp, ChevronDown, Save, LayoutGrid,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

const SIZE_CLASS = {
  sm: "",
  md: "md:col-span-2 xl:col-span-2",
  lg: "md:col-span-2 xl:col-span-4",
};

const SOURCE_BADGE = {
  live: { label: "LIVE", cls: "bg-[#dcfce7] text-[#15803d]" },
  demo: { label: "DEMO", cls: "bg-[#fef3c7] text-[#92400e]" },
  billed: { label: "BILLED", cls: "bg-[#dbeafe] text-[#1d4ed8]" },
  modeled: { label: "MODELED", cls: "bg-[#f4f4f5] text-[#52525b]" },
};

export default function EconDashboards({ client }) {
  const c = client || api.econ;
  const [dashboards, setDashboards] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [widgetData, setWidgetData] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState([]);
  const [draftName, setDraftName] = useState("");
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const dragIdx = useRef(null);

  const catalogById = useMemo(() => Object.fromEntries(catalog.map((w) => [w.id, w])), [catalog]);
  const active = dashboards.find((d) => d.id === activeId);

  useEffect(() => {
    let cancelled = false;
    Promise.all([c.dashboards(), c.widgets(), c.widgetData()])
      .then(([dashs, cat, data]) => {
        if (cancelled) return;
        setDashboards(dashs);
        setCatalog(cat.widgets);
        setWidgetData(data);
        setActiveId(dashs[0]?.id || null);
      })
      .catch((e) => console.error(e))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const startEdit = () => {
    setDraft((active?.widgets || []).map((w) => ({ ...w })));
    setDraftName(active?.name || "");
    setEditMode(true);
  };

  const saveEdit = async () => {
    try {
      const updated = await c.updateDashboard(activeId, { name: draftName, widgets: draft });
      setDashboards((ds) => ds.map((d) => (d.id === activeId ? updated : d)));
      setEditMode(false);
      toast.success("Dashboard saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save dashboard");
    }
  };

  const createDashboard = async () => {
    if (!newName.trim()) return;
    try {
      const created = await c.createDashboard({ name: newName.trim(), widgets: [] });
      setDashboards((ds) => [...ds, created]);
      setActiveId(created.id);
      setCreateOpen(false);
      setNewName("");
      setDraft([]);
      setDraftName(created.name);
      setEditMode(true);
      toast.success("Dashboard created — add widgets from the library");
    } catch (e) {
      toast.error("Failed to create dashboard");
    }
  };

  const deleteDashboard = async () => {
    if (!window.confirm(`Delete dashboard "${active?.name}"?`)) return;
    try {
      await c.deleteDashboard(activeId);
      const rest = dashboards.filter((d) => d.id !== activeId);
      setDashboards(rest);
      setActiveId(rest[0]?.id || null);
      setEditMode(false);
      toast.success("Dashboard deleted");
    } catch (e) {
      toast.error("Failed to delete dashboard");
    }
  };

  const move = (idx, dir) => {
    setDraft((d) => {
      const next = [...d];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return d;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const onDrop = (idx) => {
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from === null || from === idx) return;
    setDraft((d) => {
      const next = [...d];
      const [item] = next.splice(from, 1);
      next.splice(idx, 0, item);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="cs-card p-10 text-center text-muted-foreground" data-testid="econ-dash-loading">
        Loading Economics Dashboards…
      </div>
    );
  }

  const widgets = editMode ? draft : active?.widgets || [];

  return (
    <div className="space-y-5" data-testid="econ-dashboards">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <LayoutGrid className="w-4 h-4 text-[#00579e]" />
        {dashboards.map((d) => (
          <button
            key={d.id}
            data-testid={`econ-dash-tab-${d.id}`}
            onClick={() => { setActiveId(d.id); setEditMode(false); }}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors duration-150 ${
              d.id === activeId
                ? "bg-[#00579e] text-white border-[#00579e]"
                : "border-border text-muted-foreground hover:border-[#00579e] hover:text-[#00579e]"
            }`}
          >
            {d.name}
          </button>
        ))}
        <button
          data-testid="econ-new-dashboard-btn"
          onClick={() => setCreateOpen(true)}
          className="px-3 py-1.5 text-xs font-medium rounded-full border border-dashed border-border text-muted-foreground hover:border-[#00579e] hover:text-[#00579e] inline-flex items-center gap-1 transition-colors duration-150"
        >
          <Plus className="w-3 h-3" /> New Dashboard
        </button>

        <div className="ml-auto flex items-center gap-2">
          {!editMode && active && (
            <>
              <Button variant="outline" size="sm" data-testid="econ-edit-btn" onClick={startEdit}>
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Customize
              </Button>
              {!active.is_default && (
                <Button variant="outline" size="sm" data-testid="econ-delete-btn" onClick={deleteDashboard} className="text-[#be185d] hover:text-[#be185d]">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </>
          )}
          {editMode && (
            <>
              <Button variant="outline" size="sm" data-testid="econ-add-widgets-btn" onClick={() => setGalleryOpen(true)}>
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Widgets
              </Button>
              <Button variant="outline" size="sm" data-testid="econ-cancel-btn" onClick={() => setEditMode(false)}>
                Cancel
              </Button>
              <Button size="sm" data-testid="econ-save-btn" onClick={saveEdit} className="bg-[#00579e] hover:bg-[#00477f]">
                <Save className="w-3.5 h-3.5 mr-1.5" /> Save
              </Button>
            </>
          )}
        </div>
      </div>

      {editMode && (
        <div className="flex items-center gap-3 flex-wrap">
          <Input
            data-testid="econ-dash-name-input"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            className="max-w-xs h-8 text-sm"
            placeholder="Dashboard name"
          />
          <span className="text-[11px] text-muted-foreground">
            Drag widgets (or use arrows) to reorder · pick a size · remove with ✕
          </span>
        </div>
      )}

      {active?.description && !editMode && (
        <p className="text-xs text-muted-foreground">{active.description}</p>
      )}

      {/* Widget grid */}
      {widgets.length === 0 ? (
        <div className="cs-card p-12 text-center" data-testid="econ-empty-state">
          <p className="text-sm text-muted-foreground">This dashboard has no widgets yet.</p>
          <Button size="sm" className="mt-4 bg-[#00579e] hover:bg-[#00477f]" onClick={() => { if (!editMode) startEdit(); setGalleryOpen(true); }} data-testid="econ-empty-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add widgets
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {widgets.map((w, idx) => {
            const def = catalogById[w.widget_id];
            if (!def) return null;
            return (
              <div
                key={`${w.widget_id}-${idx}`}
                data-testid={`econ-widget-${w.widget_id}`}
                className={`cs-card p-5 ${SIZE_CLASS[w.size] ?? SIZE_CLASS.md} ${editMode ? "ring-1 ring-dashed ring-[#00579e]/40" : ""}`}
                draggable={editMode}
                onDragStart={() => { dragIdx.current = idx; }}
                onDragOver={(e) => editMode && e.preventDefault()}
                onDrop={() => editMode && onDrop(idx)}
              >
                <div className="flex items-start gap-2 mb-2">
                  {editMode && <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab shrink-0 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="cs-heading text-sm font-semibold tracking-tight leading-snug">{def.title}</h3>
                      {widgetData?.[w.widget_id]?.source && SOURCE_BADGE[widgetData[w.widget_id].source] && (
                        <span
                          data-testid={`econ-source-badge-${w.widget_id}`}
                          className={`cs-mono text-[9px] px-1.5 py-0.5 rounded-sm font-semibold tracking-wide shrink-0 ${SOURCE_BADGE[widgetData[w.widget_id].source].cls}`}
                        >
                          {SOURCE_BADGE[widgetData[w.widget_id].source].label}
                        </span>
                      )}
                    </div>
                    {!editMode && <p className="text-[11px] text-muted-foreground mt-0.5">{def.description}</p>}
                  </div>
                  {editMode && (
                    <div className="flex items-center gap-1 shrink-0">
                      <select
                        data-testid={`econ-size-select-${idx}`}
                        value={w.size}
                        onChange={(e) => setDraft((d) => d.map((x, i) => (i === idx ? { ...x, size: e.target.value } : x)))}
                        className="text-[11px] border border-border rounded-sm px-1 py-0.5 bg-white"
                      >
                        <option value="sm">S</option>
                        <option value="md">M</option>
                        <option value="lg">L</option>
                      </select>
                      <button data-testid={`econ-move-up-${idx}`} onClick={() => move(idx, -1)} className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={idx === 0}>
                        <ChevronUp className="w-3.5 h-3.5" />
                      </button>
                      <button data-testid={`econ-move-down-${idx}`} onClick={() => move(idx, 1)} className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={idx === widgets.length - 1}>
                        <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                      <button data-testid={`econ-remove-${idx}`} onClick={() => setDraft((d) => d.filter((_, i) => i !== idx))} className="p-0.5 text-[#be185d] hover:text-[#9f1239]">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                <EconWidget def={def} data={widgetData?.[w.widget_id]} size={w.size} />
              </div>
            );
          })}
        </div>
      )}

      <WidgetGallery
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        catalog={catalog}
        addedIds={draft.map((w) => w.widget_id)}
        onAdd={(w) => {
          setDraft((d) => [...d, { widget_id: w.id, size: w.default_size }]);
          toast.success(`Added "${w.title}"`);
        }}
      />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-sm" data-testid="econ-create-dialog">
          <DialogHeader>
            <DialogTitle>New Dashboard</DialogTitle>
            <DialogDescription>Name your dashboard, then pick widgets from the library.</DialogDescription>
          </DialogHeader>
          <Input
            data-testid="econ-create-name-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Storage Deep-Dive"
            onKeyDown={(e) => e.key === "Enter" && createDashboard()}
          />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button size="sm" data-testid="econ-create-confirm-btn" onClick={createDashboard} className="bg-[#00579e] hover:bg-[#00477f]">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
